import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Space,
  Tag,
  Empty,
  Spin,
  Alert,
  Divider,
  Popconfirm,
  Tooltip,
  message,
} from 'antd';
import {
  ShoppingOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import orderService from '../../services/orderService';
import './OrdersPage.scss';

const { Title, Text } = Typography;

const OrdersPage = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartStore } = useStores();
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [payingOrderId, setPayingOrderId] = useState(null);

  useEffect(() => {
    cartStore.fetchOrders();
  }, [cartStore]);

  const handlePayNow = async (orderId) => {
    setPayingOrderId(orderId);
    const result = await orderService.createPaymentSession(orderId);

    if (result.success) {
      window.location.href = result.data.checkout_url;
    } else {
      message.error(result.error || t('orders.paymentFailed'));
      setPayingOrderId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      PROCESSING: 'cyan',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      PENDING: t('orders.status.pendingPayment'),
      CONFIRMED: t('orders.status.confirmed'),
      PROCESSING: t('orders.status.processing'),
      SHIPPED: t('orders.status.shipped'),
      DELIVERED: t('orders.status.delivered'),
      CANCELLED: t('orders.status.cancelled'),
    };
    return texts[status] || status;
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleCancelOrder = async (orderId) => {
    setCancellingOrderId(orderId);
    const result = await cartStore.cancelOrder(orderId);
    setCancellingOrderId(null);

    if (result.success) {
      message.success(t('orders.cancelSuccess'));
    } else {
      message.error(result.error || t('orders.cancelFailed'));
    }
  };

  if (cartStore.ordersLoading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('orders.loading')} />
      </div>
    );
  }

  if (cartStore.error) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('common.error')}
          description={cartStore.error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!cartStore.orders || cartStore.orders.length === 0) {
    return (
      <div className="orders-page">
        <div className="sweet-container">
          <Card className="empty-orders-card">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size="large">
                  <Title level={3}>{t('orders.empty.title')}</Title>
                  <Text type="secondary">
                    {t('orders.empty.description')}
                  </Text>
                </Space>
              }
            >
              <Button
                type="primary"
                size="large"
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/products')}
              >
                {t('orders.empty.browseProducts')}
              </Button>
            </Empty>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="sweet-container">
        <div className="orders-header">
          <Title level={2}>
            <ShoppingOutlined /> {t('orders.title')}
          </Title>
          <Text type="secondary">{t('orders.totalOrders', { count: cartStore.orders.length })}</Text>
        </div>

        <Divider />

        <Row gutter={[16, 16]}>
          {cartStore.orders.map((order) => (
            <Col xs={24} key={order.id}>
              <Card
                className="order-card"
                hoverable
                onClick={() => handleViewOrder(order.id)}
              >
                <Row gutter={16} align="middle">
                  {/* Order Info */}
                  <Col xs={24} sm={12} md={8}>
                    <Space direction="vertical" size="small">
                      <Text strong className="order-number">
                        {order.order_number}
                      </Text>
                      <Text type="secondary">
                        {new Date(order.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Text>
                      <Space>
                        <EnvironmentOutlined />
                        {order.partner ? (
                          <Text
                            className="partner-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/partners/${order.partner}`);
                            }}
                          >
                            {order.partner_name}
                          </Text>
                        ) : (
                          <Text>{order.partner_name}</Text>
                        )}
                      </Space>
                    </Space>
                  </Col>

                  {/* Status & Total */}
                  <Col xs={24} sm={12} md={8}>
                    <Space direction="vertical" size="small">
                      <Tag color={getStatusColor(order.status)} className="status-tag">
                        {getStatusText(order.status)}
                      </Tag>
                      <div>
                        <Text type="secondary">{t('common.total')}: </Text>
                        <Text strong style={{ fontSize: 18, color: '#FF9A76' }}>
                          €{parseFloat(order.total_amount).toFixed(2)}
                        </Text>
                      </div>
                      <Text type="secondary">
                        {t('orders.itemCount', { count: order.items?.length || 0 })}
                      </Text>
                    </Space>
                  </Col>

                  {/* Actions */}
                  <Col xs={24} md={8}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {/* Pay Now button for pending orders */}
                      {order.status === 'PENDING' && (
                        <Button
                          type="primary"
                          icon={<CreditCardOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePayNow(order.id);
                          }}
                          loading={payingOrderId === order.id}
                          block
                        >
                          {t('orders.payNow')}
                        </Button>
                      )}
                      <Button
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewOrder(order.id);
                        }}
                        block
                      >
                        {t('orders.viewDetails')}
                      </Button>
                      {order.can_cancel && (
                        <>
                          <Popconfirm
                            title={t('orders.cancelOrder')}
                            description={t('orders.cancelConfirm')}
                            onConfirm={() => handleCancelOrder(order.id)}
                            okText={t('common.yes')}
                            cancelText={t('common.no')}
                          >
                            <Button
                              danger
                              icon={<CloseCircleOutlined />}
                              loading={cancellingOrderId === order.id}
                              block
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t('orders.cancelOrder')}
                            </Button>
                          </Popconfirm>
                          <Text type="warning" style={{ fontSize: 11, display: 'block', textAlign: 'center' }}>
                            <Tooltip title={t('orders.cancelPolicy')}>
                              <InfoCircleOutlined />{' '}
                              {order.hours_until_cancel_deadline > 0
                                ? t('orders.cancelPolicyRemaining', { hours: order.hours_until_cancel_deadline })
                                : t('orders.cancelPolicy')
                              }
                            </Tooltip>
                          </Text>
                        </>
                      )}
                    </Space>
                  </Col>
                </Row>

                {/* Order Items Preview */}
                {order.items && order.items.length > 0 && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <div className="order-items-preview">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Items:{' '}
                        {order.items.map((item, idx) => (
                          <span key={item.id}>
                            {item.product_name} ×{item.quantity}
                            {idx < order.items.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </Text>
                    </div>
                  </>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
});

export default OrdersPage;
